/**
 * 후속 공고(무순위·잔여세대 · 임의공급 · 취소분 재공급 · 추가 모집) — 기존 현장 상세의 「일정」에 잇는다.
 *
 * 왜(10/27 실측):
 *   청약홈 무순위/취소분 공고는 «새 공고번호» 로 들어온다. 현장은 이미 원 공고번호(source_ids.house_manage_no)를 들고 있어서
 *   notice-instant 의 이름 연결이 `name_conflict` 로 끝나고, 상세 페이지는 그 공고를 «모른다».
 *   최근 60일 후속 공고 129건 중 같은 이름 현장 127곳 · 그중 56곳은 현장이 다른 공고를 가리켜 상세 일정에 무순위 접수가 없었다
 *   (예: 화서역 호수공원 아너스빌 무순위 11/2 · 광안센텀 비스타동원 조합원 취소분 10/26~27).
 *   cafe(nk-series)는 이 공고로 글을 쓰고 상세로 링크하는데, 정작 상세는 그 일정을 말하지 않았다.
 *
 * 규칙:
 *   ⛔ 현장 연결(source_ids)은 건드리지 않는다 — 읽기 전용 «보조 일정» 이다.
 *   ⛔ 이름이 «정확히» 같은 공고만(괄호·공백·회차 표식 제거 뒤 동일) + 시도 일치. 부분 일치로 남의 단지를 잇지 않는다.
 *   ⛔ 날짜를 지어내지 않는다. 접수 시작일이 없는 공고는 행이 없다.
 */

export interface FollowUpNoticeRow {
  id: number | string;
  house_nm: string | null;
  house_manage_no?: string | null;
  region_nm?: string | null;
  notice_kind?: string | null;
  rcept_bgnde?: string | null;
  rcept_endde?: string | null;
  przwner_presnatn_de?: string | null;
  tot_supply_hshld_co?: number | null;
  announcement_date?: string | null;
}

export type FollowUpKind = 'remndr' | 'opt' | 'resupply' | 'extra';

export interface FollowUpNotice {
  id: string;
  kind: FollowUpKind;
  label: string;
  rceptBgnde: string;
  rceptEndde: string | null;
  winnerDate: string | null;
  units: number | null;
  announcementDate: string | null;
}

/** 이름 안의 회차·후속 표식. */
const MARKER = /조합원\s*취소분|취소분|무순위|잔여\s*세대|잔여|임의\s*공급|재공급|추가\s*(?:입주자)?\s*모집(?:\s*공고)?|\d+\s*차\s*(?:무순위|추가)/g;

/** 비교용 이름 — 괄호 덩어리·후속 표식·공백·구두점을 뗀다. */
export function baseNoticeName(name: string | null | undefined): string {
  return String(name ?? '')
    .replace(/\([^)]*\)|\[[^\]]*\]|（[^）]*）/g, '')
    .replace(MARKER, '')
    .replace(/[\s·.,\-_]/g, '')
    .toLowerCase();
}

/** 후속 공고 종류. 후속이 아니면 null(정규 1·2순위 공고). */
export function followUpKind(row: Pick<FollowUpNoticeRow, 'notice_kind' | 'house_nm'>): FollowUpKind | null {
  const k = String(row.notice_kind ?? '').trim();
  if (k === 'remndr') return 'remndr';
  if (k === 'opt') return 'opt';
  const n = String(row.house_nm ?? '');
  if (/무순위|잔여/.test(n)) return 'remndr';
  if (/임의\s*공급/.test(n)) return 'opt';
  if (/취소분|재공급/.test(n)) return 'resupply';
  if (/추가\s*(?:입주자)?\s*모집/.test(n)) return 'extra';
  return null;
}

export const FOLLOW_UP_LABEL: Record<FollowUpKind, string> = {
  remndr: '무순위·잔여세대 접수',
  opt: '임의공급 접수',
  resupply: '취소분 재공급 접수',
  extra: '추가 모집 접수',
};

/**
 * 공고 «자신» 의 접수 행 라벨 — 정규 공고면 「1·2순위 접수」, 무순위·임의공급 공고면 그 이름.
 * ⚠️ 무순위·임의공급 공고에는 순위가 없다(10/27 실측: 현장 대표 공고가 remndr 23·opt 44곳인데 일정표는 「1·2순위 접수」였다).
 * ⚠️ notice_kind='apt' 인 취소분 재공급은 순위 접수가 있을 수 있어 종전 라벨을 유지한다.
 */
export function applyRowLabel(sub: { notice_kind?: string | null; house_nm?: string | null } | null | undefined): string {
  const k = String(sub?.notice_kind ?? '').trim();
  if (k === 'remndr') return FOLLOW_UP_LABEL.remndr;
  if (k === 'opt') return FOLLOW_UP_LABEL.opt;
  if (k === 'pbl_pvt_rent') return '청약 접수';
  return '1·2순위 접수';
}

const ymd = (v: string | null | undefined): string | null => {
  const s = String(v ?? '').trim();
  const m8 = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m8) return `${m8[1]}-${m8[2]}-${m8[3]}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};
const shiftDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

/** 시도 표기 정규화 — 「부산광역시」·「부산」 둘 다 「부산」. */
const sidoKey = (v: string | null | undefined) => String(v ?? '').trim().replace(/(특별자치시|특별자치도|광역시|특별시|도)$/, '').slice(0, 2);

export interface PickFollowUpsInput {
  siteName: string | null | undefined;
  siteRegion?: string | null;
  /** 현장 대표 공고 — 같은 공고는 후속이 아니다. */
  mainSub?: Pick<FollowUpNoticeRow, 'id' | 'house_manage_no'> | null;
  /** 묶음 공고 id(source_ids.subscription_bundle) — 이미 대표 공고에 합쳐진 것. */
  excludeIds?: readonly string[];
  rows: readonly FollowUpNoticeRow[];
  today: string;
  /** 지난 공고를 남기는 창(일). 일대기라 지난 행도 남기되, 오래된 것은 뺀다. */
  pastDays?: number;
  /** 앞으로 창(일). */
  futureDays?: number;
  max?: number;
}

/** 후속 공고 고르기 — 순수 함수. 접수 시작일 오름차순, 최근 max 건. */
export function pickFollowUps(i: PickFollowUpsInput): FollowUpNotice[] {
  const base = baseNoticeName(i.siteName);
  if (base.length < 3) return [];
  const lo = shiftDays(i.today, -(i.pastDays ?? 180));
  const hi = shiftDays(i.today, i.futureDays ?? 90);
  const skip = new Set<string>([...(i.excludeIds ?? []).map(String), ...(i.mainSub?.id != null ? [String(i.mainSub.id)] : [])]);
  const skipHmn = String(i.mainSub?.house_manage_no ?? '').trim();
  const region = sidoKey(i.siteRegion);
  /* 같은 날 같은 종류 공고가 여럿(공고번호가 다르면 별개 회차 공급 — 예: 같은 날 무순위 2세대 + 1세대)이면 한 행으로 합치고 세대는 더한다.
   * 같은 공고번호(오퍼레이션 중복 적재)는 한 번만 센다. */
  const byKey = new Map<string, FollowUpNotice & { _hmns: Set<string> }>();
  for (const r of i.rows) {
    if (skip.has(String(r.id))) continue;
    const hmn = String(r.house_manage_no ?? '').trim();
    if (skipHmn && hmn === skipHmn) continue;
    const kind = followUpKind(r);
    if (!kind) continue;
    if (baseNoticeName(r.house_nm) !== base) continue;
    if (region && r.region_nm && sidoKey(r.region_nm) !== region) continue;
    const b = ymd(r.rcept_bgnde);
    if (!b || b < lo || b > hi) continue;
    const e = ymd(r.rcept_endde);
    const key = `${kind}|${b}|${e ?? ''}`;
    const u = Number(r.tot_supply_hshld_co);
    const units = Number.isFinite(u) && u > 0 ? u : null;
    const id = hmn || `id:${r.id}`;
    const prev = byKey.get(key);
    if (prev) {
      if (prev._hmns.has(id)) continue;
      prev._hmns.add(id);
      prev.units = prev.units != null && units != null ? prev.units + units : null; // 한쪽이라도 모르면 합계를 말하지 않는다
      continue;
    }
    byKey.set(key, {
      id: String(r.id), kind, label: FOLLOW_UP_LABEL[kind],
      rceptBgnde: b, rceptEndde: e && e !== b ? e : null,
      winnerDate: ymd(r.przwner_presnatn_de),
      units,
      announcementDate: ymd(r.announcement_date),
      _hmns: new Set([id]),
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const out: FollowUpNotice[] = [...byKey.values()].map(({ _hmns, ...f }) => f);
  out.sort((a, b) => a.rceptBgnde.localeCompare(b.rceptBgnde));
  const max = i.max ?? 3;
  return out.length > max ? out.slice(out.length - max) : out;
}

/* ───────── 무순위 수식어(킥·title) — 「{단지명} 무순위」 검색에 이 상세가 «일정 행으로» 답할 때만 ─────────
 *   대상 = 무순위·잔여(remndr) 후속 공고 중 접수가 아직 안 끝났거나(진행·예정) 끝난 지 UNRANKED_GRACE_DAYS 이내.
 *   ⛔ 일정 행이 없으면 수식어도 없다(답 없는 수식어 = 검색어 낚시 금지 · kick-detail 규칙). */
export const UNRANKED_KW = '무순위';
export const UNRANKED_GRACE_DAYS = 7;

/** 순수 — 수식어를 걸 무순위 회차(가장 이른 진행·예정 회차 · 없으면 최근에 끝난 회차). 없으면 null. */
export function unrankedRound(followUps: readonly FollowUpNotice[] | null | undefined, today: string): FollowUpNotice | null {
  const lo = shiftDays(today, -UNRANKED_GRACE_DAYS);
  const rows = (followUps ?? []).filter((f) => f?.kind === 'remndr' && !!f.rceptBgnde && (f.rceptEndde ?? f.rceptBgnde) >= lo);
  if (!rows.length) return null;
  const live = rows.filter((f) => (f.rceptEndde ?? f.rceptBgnde) >= today).sort((a, b) => a.rceptBgnde.localeCompare(b.rceptBgnde));
  return live[0] ?? [...rows].sort((a, b) => b.rceptBgnde.localeCompare(a.rceptBgnde))[0];
}

/** 순수 — title 두 번째 검색어 자리에 「무순위」를 앞세운다(있을 때만 · 최대 n · 같은 낱말 1회). */
export function withUnrankedTitleKw(kw: readonly string[] | null | undefined, has: boolean, n = 2): string[] {
  const base = (kw ?? []).filter(Boolean);
  if (!has || base.includes(UNRANKED_KW)) return base.slice(0, n);
  return [UNRANKED_KW, ...base].slice(0, n);
}

export const FOLLOW_UP_COLS ='id, house_nm, house_manage_no, region_nm, notice_kind, rcept_bgnde, rcept_endde, przwner_presnatn_de, tot_supply_hshld_co, announcement_date';

/** ilike 패턴용 이름 머리 — 괄호 앞까지, 공백은 %(띄어쓰기 차이 흡수). 특수문자는 뺀다. */
export function followUpLikePattern(siteName: string | null | undefined): string | null {
  const head = String(siteName ?? '').replace(/\(.*$/, '').replace(/[%_\\,()'"]/g, '').trim();
  if (head.replace(/\s/g, '').length < 3) return null;
  return `${head.split(/\s+/).filter(Boolean).join('%')}%`;
}

/**
 * 조회 — 선택 데이터. 실패·지연은 빈 배열(페이지를 죽이지 않는다).
 * house_nm 트라이그램 색인(idx_apt_house_nm_trgm) · 행 3천여 · limit 20.
 */
export async function loadFollowUpNotices(
  sb: any,
  site: { name?: string | null; region?: string | null; source_ids?: unknown } | null | undefined,
  mainSub: Pick<FollowUpNoticeRow, 'id' | 'house_manage_no'> | null | undefined,
  today: string,
): Promise<FollowUpNotice[]> {
  const pat = followUpLikePattern(site?.name);
  if (!pat) return [];
  try {
    const { data, error } = await sb.from('apt_subscriptions').select(FOLLOW_UP_COLS).ilike('house_nm', pat).order('id', { ascending: false }).limit(20);
    if (error || !Array.isArray(data)) return [];
    const bundle = String(((site?.source_ids ?? {}) as Record<string, unknown>).subscription_bundle ?? '')
      .split(',').map((s) => s.trim()).filter(Boolean);
    return pickFollowUps({ siteName: site?.name, siteRegion: site?.region ?? null, mainSub: mainSub ?? null, excludeIds: bundle, rows: data, today });
  } catch {
    return [];
  }
}
